import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown, Save, ImagePlus, Music, Upload } from "lucide-react";
import { api } from "@/lib/api";
import MediaPicker from "@/components/MediaPicker";

/**
 * HomePanel — panneau admin "Accueil".
 *
 * Deux blocs :
 *  1. Vignettes d'accueil — 3 arches (Réserver / Le refuge / Expériences),
 *     chaque arche pouvant contenir plusieurs images en mini-galerie
 *     automatique (fondu enchaîné + ken-burns). Réordre par flèches ↑↓,
 *     ajout via MediaPicker, suppression avec confirmation.
 *
 *  2. Sons du site — 2 emplacements :
 *       • ambient : musique ambiante (toutes les pages sauf entrée / admin)
 *       • door    : son de la page d'entrée (porte)
 *     Upload direct MP3/OGG.
 *
 * Tout est stocké dans `content.homeArches` et `content.siteSounds`
 * via l'endpoint existant PUT /api/admin/content.
 */

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const ARCHES = [
  { key: "reserver",    label: "Réserver",   sub: "Voir les disponibilités" },
  { key: "refuge",      label: "Le refuge",  sub: "Explorer le refuge" },
  { key: "experiences", label: "Expériences", sub: "Découvrir les expériences" },
];

// URL preview d'une image (chemin statique OU basename uploadé)
const previewUrl = (val) => {
  if (!val) return "";
  if (val.startsWith("http") || val.startsWith("/")) return val;
  return `${BACKEND}/api/media/${encodeURIComponent(val)}`;
};

export default function HomePanel({ site = "refuge" }) {
  const [content, setContent] = useState({});
  const [uploaded, setUploaded] = useState([]);
  const [picker, setPicker] = useState(null); // { archKey } | { soundSlot }
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [c, m] = await Promise.all([api.getContent(site), api.listMedia(site)]);
    setContent(c || {});
    setUploaded(m || []);
  };
  useEffect(() => { load(); }, [site]);

  const arches = content?.homeArches || {};
  const sounds = content?.siteSounds || {};

  const flash = (t) => { setMsg(t); window.setTimeout(() => setMsg(""), 2200); };

  const save = async (patch) => {
    const next = { ...content, ...patch };
    setBusy(true);
    try {
      await api.putContent(site, next);
      setContent(next);
      flash("✓ Enregistré");
    } catch (e) {
      flash("Erreur : " + (e?.response?.data?.detail || e?.message || "impossible d'enregistrer"));
    } finally { setBusy(false); }
  };

  const setArche = (key, list) => save({ homeArches: { ...arches, [key]: list } });
  const setSound = (slot, val) => save({ siteSounds: { ...sounds, [slot]: val } });

  // Ajout d'une image (depuis MediaPicker)
  const onPickImage = (filename) => {
    if (!picker) return;
    if (picker.archKey) {
      const current = arches[picker.archKey] || [];
      setArche(picker.archKey, [...current, filename]);
    }
    setPicker(null);
  };

  // Upload libre de son (MP3/OGG) sans passer par le picker
  const uploadSound = async (slot, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const ext = (file.name.split(".").pop() || "mp3").toLowerCase();
      const ts = new Date().toISOString().replace(/[:T-]/g, "").slice(0, 14);
      const filename = `sound-${slot}-${ts}.${ext}`;
      await api.uploadMedia(file, filename, site);
      await load();
      await setSound(slot, filename);
    } catch (e) {
      flash("Erreur upload : " + (e?.message || "inconnue"));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-10" data-testid="home-panel">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-light text-refuge-text">Accueil</h2>
          <p className="font-body text-sm text-refuge-soft mt-1">
            Vignettes des 3 fenêtres · sons ambiants du site
          </p>
        </div>
        {msg && <span className="font-body text-sm text-refuge-ruby" data-testid="home-panel-msg">{msg}</span>}
      </header>

      {/* ==== BLOC 1 · VIGNETTES D'ACCUEIL ==== */}
      <section className="bg-white border border-refuge-border rounded-2xl p-6 md:p-7">
        <div className="mb-4 pb-4 border-b border-refuge-border/60">
          <p className="font-body text-[10px] tracking-[0.22em] uppercase text-refuge-ruby mb-1">Bloc 1</p>
          <h3 className="font-heading text-xl font-light text-refuge-text">Vignettes des 3 fenêtres</h3>
          <p className="font-body text-xs text-refuge-soft mt-2 leading-relaxed">
            Ajoutez une ou plusieurs images par fenêtre. Si plusieurs images sont présentes,
            elles se succèdent automatiquement avec un fondu doux (ken-burns lent conservé).
            Aucune image = comportement par défaut (image d&apos;origine).
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {ARCHES.map((a) => (
            <ArcheColumn
              key={a.key}
              arche={a}
              images={arches[a.key] || []}
              onAdd={() => setPicker({ archKey: a.key })}
              onRemove={(i) => {
                if (!window.confirm("Retirer cette image de la fenêtre ?")) return;
                const list = arches[a.key] || [];
                setArche(a.key, list.filter((_, j) => j !== i));
              }}
              onMove={(i, dir) => {
                const list = [...(arches[a.key] || [])];
                const j = i + dir;
                if (j < 0 || j >= list.length) return;
                [list[i], list[j]] = [list[j], list[i]];
                setArche(a.key, list);
              }}
            />
          ))}
        </div>
      </section>

      {/* ==== BLOC 2 · SONS DU SITE ==== */}
      <section className="bg-white border border-refuge-border rounded-2xl p-6 md:p-7" data-testid="site-sounds">
        <div className="mb-4 pb-4 border-b border-refuge-border/60">
          <p className="font-body text-[10px] tracking-[0.22em] uppercase text-refuge-ruby mb-1">Bloc 2</p>
          <h3 className="font-heading text-xl font-light text-refuge-text">Sons du site</h3>
          <p className="font-body text-xs text-refuge-soft mt-2 leading-relaxed">
            Formats acceptés : MP3, OGG. Après upload, l&apos;écoute est disponible immédiatement en preview
            (rafraîchir une fois).
          </p>
        </div>

        <div className="grid gap-5 md:grid-cols-2">
          <SoundSlot
            slot="ambient"
            title="Musique ambiante (site)"
            desc="Bouton bas-gauche des pages publiques (hors entrée & admin)."
            currentValue={sounds.ambient}
            onUpload={(f) => uploadSound("ambient", f)}
            onClear={() => setSound("ambient", "")}
            busy={busy}
          />
          <SoundSlot
            slot="door"
            title="Son de la porte (entrée)"
            desc="Ambiance sonore de la page d'entrée / animation de la porte."
            currentValue={sounds.door}
            onUpload={(f) => uploadSound("door", f)}
            onClear={() => setSound("door", "")}
            busy={busy}
          />
        </div>
      </section>

      {/* ==== BLOC 3 · ACCÈS "GUIDE DU VOYAGEUR" DANS LE MENU ==== */}
      <MenuGuideAccessBlock
        cfg={content?.menuGuideAccess}
        onSave={(patch) => save({ menuGuideAccess: { ...(content?.menuGuideAccess || {}), ...patch } })}
        busy={busy}
      />

      {/* ==== BLOC 4 · NAVIGATION — RETOUR AU MENU ==== */}
      <MenuBackButtonBlock
        cfg={content?.menuBackButton}
        onSave={(patch) => save({ menuBackButton: { ...(content?.menuBackButton || {}), ...patch } })}
        busy={busy}
      />

      {picker && (
        <MediaPicker
          uploaded={uploaded}
          onPick={onPickImage}
          onClose={() => setPicker(null)}
          onUploaded={load}
          site={site}
          accept="image/*"
        />
      )}
    </div>
  );
}

// ============================================================
// Colonne d'une arche : miniatures ordonnées + actions
// ============================================================
function ArcheColumn({ arche, images, onAdd, onRemove, onMove }) {
  return (
    <div className="rounded-xl border border-refuge-border/70 p-4 bg-refuge-bg" data-testid={`arche-col-${arche.key}`}>
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <p className="font-heading text-base text-refuge-text">{arche.label}</p>
          <p className="font-body text-[11px] text-refuge-soft">{arche.sub}</p>
        </div>
        <span className="text-[10px] tracking-widest uppercase text-refuge-soft/70">{images.length} img</span>
      </div>

      {images.length === 0 && (
        <p className="font-body italic text-xs text-refuge-soft/80 mb-3">
          Aucune image ajoutée · l&apos;image d&apos;origine sera utilisée.
        </p>
      )}

      <ul className="space-y-2 mb-3">
        {images.map((img, i) => (
          <li
            key={`${img}-${i}`}
            className="flex items-center gap-3 rounded-lg p-2 bg-white border border-refuge-border/60"
            data-testid={`arche-item-${arche.key}-${i}`}
          >
            <img
              src={previewUrl(img)}
              alt=""
              className="w-14 h-14 object-cover rounded"
              onError={(e) => { e.currentTarget.style.opacity = 0.3; }}
            />
            <span className="flex-1 min-w-0 font-mono text-[11px] text-refuge-soft truncate">{img}</span>
            <div className="flex flex-col gap-0.5">
              <button
                type="button"
                onClick={() => onMove(i, -1)}
                disabled={i === 0}
                className="p-1 disabled:opacity-30 text-refuge-soft hover:text-refuge-ruby"
                aria-label="Monter"
              ><ArrowUp size={14} /></button>
              <button
                type="button"
                onClick={() => onMove(i, +1)}
                disabled={i === images.length - 1}
                className="p-1 disabled:opacity-30 text-refuge-soft hover:text-refuge-ruby"
                aria-label="Descendre"
              ><ArrowDown size={14} /></button>
            </div>
            <button
              type="button"
              onClick={() => onRemove(i)}
              className="p-1 text-refuge-ruby/70 hover:text-refuge-ruby"
              aria-label="Retirer"
              data-testid={`arche-remove-${arche.key}-${i}`}
            >
              <Trash2 size={14} />
            </button>
          </li>
        ))}
      </ul>

      <button
        type="button"
        onClick={onAdd}
        data-testid={`arche-add-${arche.key}`}
        className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full border border-dashed border-refuge-ruby text-refuge-ruby font-body text-xs hover:bg-refuge-ruby hover:text-white transition-colors"
      >
        <ImagePlus size={14} /> Ajouter une image
      </button>
    </div>
  );
}

// ============================================================
// Slot son : preview + upload + clear
// ============================================================
function SoundSlot({ slot, title, desc, currentValue, onUpload, onClear, busy }) {
  const hasCustom = !!currentValue;
  const src = hasCustom ? previewUrl(currentValue) : "";
  return (
    <div className="rounded-xl border border-refuge-border/70 p-4 bg-refuge-bg" data-testid={`sound-slot-${slot}`}>
      <div className="flex items-center gap-3 mb-2">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: "rgba(232, 184, 189, 0.18)", color: "var(--cr-ruby)" }}
        >
          <Music size={16} strokeWidth={1.3} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-heading text-base text-refuge-text">{title}</p>
          <p className="font-body text-[11px] text-refuge-soft">{desc}</p>
        </div>
      </div>

      {hasCustom ? (
        <>
          <audio src={src} controls className="w-full mt-3" data-testid={`sound-player-${slot}`} />
          <p className="mt-2 font-mono text-[10px] text-refuge-soft truncate">{currentValue}</p>
          <div className="mt-3 flex gap-2">
            <label className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full border border-refuge-ruby text-refuge-ruby text-xs font-body cursor-pointer hover:bg-refuge-ruby hover:text-white">
              <Upload size={13} /> Remplacer
              <input
                type="file"
                accept="audio/*"
                className="hidden"
                disabled={busy}
                data-testid={`sound-upload-${slot}`}
                onChange={(e) => onUpload(e.target.files?.[0])}
              />
            </label>
            <button
              type="button"
              onClick={() => window.confirm("Rétablir le son par défaut ?") && onClear()}
              data-testid={`sound-clear-${slot}`}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-refuge-border text-refuge-soft text-xs font-body hover:text-refuge-ruby hover:border-refuge-ruby"
            >
              <Trash2 size={13} /> Défaut
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="font-body italic text-xs text-refuge-soft/80 mt-3 mb-2">
            Son par défaut utilisé actuellement (fichier livré avec le site).
          </p>
          <label className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full border border-dashed border-refuge-ruby text-refuge-ruby font-body text-xs cursor-pointer hover:bg-refuge-ruby hover:text-white transition-colors">
            <Upload size={14} /> Uploader un son personnalisé
            <input
              type="file"
              accept="audio/*"
              className="hidden"
              disabled={busy}
              data-testid={`sound-upload-${slot}`}
              onChange={(e) => onUpload(e.target.files?.[0])}
            />
          </label>
        </>
      )}
    </div>
  );
}

// ============================================================
// Bloc 3 · Accès "Guide du voyageur" dans le menu
// ============================================================
const GUIDE_ICON_CHOICES = [
  { key: "book_open", label: "Livre ouvert" },
  { key: "book",      label: "Livre" },
  { key: "bookmark",  label: "Marque-page" },
];
const GUIDE_DEFAULTS = {
  visible: true, label: "Guide du voyageur", cta: "Ouvrir le guide", url: "/guide", icon_key: "book_open",
};

function MenuGuideAccessBlock({ cfg, onSave, busy }) {
  const current = { ...GUIDE_DEFAULTS, ...(cfg || {}) };
  const set = (patch) => onSave(patch);
  return (
    <section className="bg-white border border-refuge-border rounded-2xl p-6 md:p-7" data-testid="menu-guide-access-block">
      <div className="mb-4 pb-4 border-b border-refuge-border/60">
        <p className="font-body text-[10px] tracking-[0.22em] uppercase text-refuge-ruby mb-1">Bloc 3</p>
        <h3 className="font-heading text-xl font-light text-refuge-text">Accès « Guide du voyageur » dans le menu</h3>
        <p className="font-body text-xs text-refuge-soft mt-2 leading-relaxed">
          Contrôle le 4<sup>e</sup> rond du menu (à côté d&apos;Avis, Tarifs, Bon cadeau).
          Ne modifie ni Avis, ni Tarifs, ni Bon cadeau.
        </p>
      </div>

      <div className="space-y-4">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={current.visible}
            onChange={(e) => set({ visible: e.target.checked })}
            disabled={busy}
            data-testid="menu-guide-visible"
            className="w-4 h-4 accent-[color:var(--refuge-ruby,#8a2532)]"
          />
          <span className="font-body text-sm text-refuge-text">Afficher ce rond dans le menu</span>
        </label>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="block font-body text-[11px] uppercase tracking-wider text-refuge-soft mb-1">Titre</label>
            <input
              type="text"
              value={current.label}
              onChange={(e) => set({ label: e.target.value })}
              disabled={busy}
              data-testid="menu-guide-label"
              className="w-full rounded-lg border border-refuge-border px-3 py-2 text-sm bg-white"
            />
          </div>
          <div>
            <label className="block font-body text-[11px] uppercase tracking-wider text-refuge-soft mb-1">Texte d&apos;action</label>
            <input
              type="text"
              value={current.cta}
              onChange={(e) => set({ cta: e.target.value })}
              disabled={busy}
              data-testid="menu-guide-cta"
              className="w-full rounded-lg border border-refuge-border px-3 py-2 text-sm bg-white"
            />
          </div>
        </div>

        <div>
          <label className="block font-body text-[11px] uppercase tracking-wider text-refuge-soft mb-1">Lien de destination</label>
          <input
            type="text"
            value={current.url}
            onChange={(e) => set({ url: e.target.value })}
            disabled={busy}
            data-testid="menu-guide-url"
            className="w-full rounded-lg border border-refuge-border px-3 py-2 text-sm bg-white font-mono"
          />
          <p className="font-body text-[11px] text-refuge-soft mt-1">Par défaut : <code>/guide</code></p>
        </div>

        <div>
          <label className="block font-body text-[11px] uppercase tracking-wider text-refuge-soft mb-2">Icône</label>
          <div className="flex flex-wrap gap-2">
            {GUIDE_ICON_CHOICES.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => set({ icon_key: c.key })}
                disabled={busy}
                data-testid={`menu-guide-icon-${c.key}`}
                className={`px-3 py-1.5 rounded-full border text-xs transition ${
                  current.icon_key === c.key
                    ? "border-refuge-ruby bg-refuge-ruby/10 text-refuge-ruby"
                    : "border-refuge-border text-refuge-soft hover:border-refuge-ruby/40"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={() => set(GUIDE_DEFAULTS)}
          disabled={busy}
          data-testid="menu-guide-reset"
          className="text-xs text-refuge-soft hover:text-refuge-ruby underline underline-offset-2"
        >
          ↻ Revenir aux valeurs par défaut
        </button>
      </div>
    </section>
  );
}

// ============================================================
// Bloc 4 · Navigation — Retour au menu (bouton flottant)
// ============================================================
const BACK_ICON_CHOICES = [
  { key: "arrow", label: "Flèche gauche ←" },
  { key: "home",  label: "Maison" },
  { key: "menu",  label: "Menu (3 traits)" },
];
const BACK_DEFAULTS = { visible: true, label: "Retour au menu", icon_key: "arrow" };

function MenuBackButtonBlock({ cfg, onSave, busy }) {
  const current = { ...BACK_DEFAULTS, ...(cfg || {}) };
  const set = (patch) => onSave(patch);
  return (
    <section className="bg-white border border-refuge-border rounded-2xl p-6 md:p-7" data-testid="menu-back-btn-block">
      <div className="mb-4 pb-4 border-b border-refuge-border/60">
        <p className="font-body text-[10px] tracking-[0.22em] uppercase text-refuge-ruby mb-1">Bloc 4</p>
        <h3 className="font-heading text-xl font-light text-refuge-text">Navigation — Retour au menu</h3>
        <p className="font-body text-xs text-refuge-soft mt-2 leading-relaxed">
          Bouton flottant discret visible en bas à droite sur toutes les pages internes.
          Renvoie toujours vers <code>/accueil</code>. Ne s&apos;affiche pas sur le menu lui-même ni dans l&apos;admin.
        </p>
      </div>

      <div className="space-y-4">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={current.visible}
            onChange={(e) => set({ visible: e.target.checked })}
            disabled={busy}
            data-testid="menu-back-visible"
            className="w-4 h-4 accent-[color:var(--refuge-ruby,#8a2532)]"
          />
          <span className="font-body text-sm text-refuge-text">Afficher le bouton sur les pages internes</span>
        </label>

        <div>
          <label className="block font-body text-[11px] uppercase tracking-wider text-refuge-soft mb-1">Texte du bouton</label>
          <input
            type="text"
            value={current.label}
            onChange={(e) => set({ label: e.target.value })}
            disabled={busy}
            data-testid="menu-back-label"
            className="w-full rounded-lg border border-refuge-border px-3 py-2 text-sm bg-white"
          />
        </div>

        <div>
          <label className="block font-body text-[11px] uppercase tracking-wider text-refuge-soft mb-2">Icône</label>
          <div className="flex flex-wrap gap-2">
            {BACK_ICON_CHOICES.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => set({ icon_key: c.key })}
                disabled={busy}
                data-testid={`menu-back-icon-${c.key}`}
                className={`px-3 py-1.5 rounded-full border text-xs transition ${
                  current.icon_key === c.key
                    ? "border-refuge-ruby bg-refuge-ruby/10 text-refuge-ruby"
                    : "border-refuge-border text-refuge-soft hover:border-refuge-ruby/40"
                }`}
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-lg bg-refuge-bg p-3 border border-refuge-border/60">
          <p className="font-body text-[11px] uppercase tracking-wider text-refuge-soft mb-2">Aperçu</p>
          <span
            className="inline-flex items-center gap-2 rounded-full text-[12px]"
            style={{
              padding: "8px 16px 8px 12px",
              background: "rgba(255, 253, 249, 0.94)",
              color: "var(--refuge-text)",
              border: "1px solid rgba(139, 37, 48, 0.28)",
              boxShadow: "0 4px 14px rgba(46, 31, 24, 0.10)",
            }}
          >
            <span style={{ color: "var(--refuge-ruby, #8a2532)" }}>
              {current.icon_key === "home" ? "⌂" : current.icon_key === "menu" ? "≡" : "←"}
            </span>
            <span>{current.label || "Retour au menu"}</span>
          </span>
        </div>

        <button
          type="button"
          onClick={() => set(BACK_DEFAULTS)}
          disabled={busy}
          data-testid="menu-back-reset"
          className="text-xs text-refuge-soft hover:text-refuge-ruby underline underline-offset-2"
        >
          ↻ Revenir aux valeurs par défaut
        </button>
      </div>
    </section>
  );
}

